/**
 * Outcome of verifying the microphone track the Voice SDK is about to send.
 *
 * The check exists because a browser leg can be established with a perfectly healthy signalling
 * path and still carry digital silence: `getUserMedia` resolves, the RTP flows, and the track sends
 * zeroes. Twilio reports no error for that — the patient simply hears nothing — so the only place it
 * can be caught is in front of the track, before the call is placed.
 */
export type MicrophoneCheckStatus =
  /** The track is live, unmuted and carrying signal at a normal level. */
  | 'ok'
  /** The care manager (or a policy) refused microphone access. */
  | 'permission-denied'
  /** No input device is available, or the browser gave no audio track. */
  | 'no-device'
  /** The track exists but is already `ended` — typically a device unplugged since it was acquired. */
  | 'track-ended'
  /** The OS/browser is holding the track muted; it produces zeroes until it is re-acquired. */
  | 'track-muted'
  /** Live and unmuted, and nothing at all was captured: no sample reached one bit of amplitude. */
  | 'silent'
  /**
   * Captured, but far below a healthy channel — a hardware mute switch, a gained-down input, a
   * microphone pointing at nothing. Deliberately NOT a reason to refuse a call: Chrome's default
   * noise suppression can push a working microphone in a silent room this low, so it is surfaced and
   * reported with its measured level rather than enforced, until the band is calibrated on real
   * hardware. See `SUSPECT_RMS_THRESHOLD` in MicrophoneCheckService.
   */
  | 'very-quiet'
  /**
   * The input is not a microphone but a device that records what the computer plays — Windows'
   * "Stereo Mix" (Spanish: "Mezcla estéreo"), virtual cables, and the like. It captures near-digital
   * silence while nothing is playing, so the level check grades it `very-quiet` and lets the call
   * through with the patient hearing nothing (found in QA on 2026-10-05, after Windows made "Mezcla
   * estéreo" the default input). Recognised by its label, so it is refused before the level is read.
   */
  | 'loopback-device'
  /**
   * The device could not be opened for a reason that says nothing about the microphone — busy, held
   * by another application, or an acquisition that lost a race. Never a reason to refuse a call: the
   * caller falls back to letting the SDK acquire the device itself, as it did before this check.
   */
  | 'acquisition-failed'
  /** The browser lacks the APIs needed to check (no Web Audio / no media devices). */
  | 'unsupported';

/** Measured input level, in the -1..1 time domain and in dBFS for anything a human reads. */
export interface MicrophoneLevel {
  /** Largest absolute sample observed: answers "was anything captured at all". */
  peak: number;
  /** Root mean square over the whole listening window: answers "how much", and is what to calibrate. */
  rms: number;
  peakDbfs: number;
  rmsDbfs: number;
}

export interface MicrophoneCheckResult extends Partial<MicrophoneLevel> {
  status: MicrophoneCheckStatus;
  /** Device label of the checked input. Hardware identification only — never PHI. */
  label?: string;
  deviceId?: string;
  /** DOMException name when acquisition itself failed (NotAllowedError, NotReadableError, ...). */
  errorName?: string;
}

/**
 * What the care manager is told for each outcome, in the terms of the one action they can take.
 *
 * Deliberately not phrased as "call failed": nothing is wrong with the call, the microphone is not
 * reaching it, and the fix is always at the device end.
 */
export const MICROPHONE_CHECK_MESSAGES: Record<
  Exclude<MicrophoneCheckStatus, 'ok' | 'unsupported' | 'acquisition-failed'>,
  string
> = {
  'permission-denied': 'Microphone access is blocked for this site. Allow it in the browser and try again.',
  'no-device': 'No working microphone was found. Check that your headset is connected and try again.',
  'track-ended': 'Your microphone stopped responding. Reconnect your headset and try again.',
  'track-muted': 'Your microphone is muted by your computer. Unmute it and try again.',
  silent: 'Your microphone is not picking up any sound. Check your headset and system input device, then try again.',
  'very-quiet': 'Your microphone is picking up very little sound — check the mute switch on your headset.',
  'loopback-device': 'Your computer is using "Stereo Mix" or a virtual cable as its microphone. Select your headset as the input device and try again.',
};

/** Outcomes the care manager is told about, whether or not they stop the call. */
export const isReportableMicrophoneFailure = (
  status: MicrophoneCheckStatus
): status is keyof typeof MICROPHONE_CHECK_MESSAGES => status in MICROPHONE_CHECK_MESSAGES;

/**
 * Outcomes that stop a call being placed.
 *
 * Only the ones where dialling is certain to waste the patient's time: nothing captured, no device,
 * a dead track, or a refused permission. `very-quiet` warns and lets the call through — a threshold
 * set by judgement rather than measurement must not be able to ground a care manager.
 */
export const isBlockingMicrophoneFailure = (status: MicrophoneCheckStatus): boolean =>
  isReportableMicrophoneFailure(status) && status !== 'very-quiet';

/**
 * Labels of inputs that record the computer's own output instead of a voice. Matched case-insensitively
 * against the device label the browser reports, which is the driver's name in the OS language.
 *
 * Deliberately specific names, not words like "mix" or "output" alone: a false match refuses a call on
 * a working microphone, which is worse than missing an obscure virtual device.
 */
export const LOOPBACK_INPUT_PATTERNS: readonly RegExp[] = [
  /stereo mix/i,
  /mezcla est[eé]reo/i,
  /mixage st[eé]r[eé]o/i,
  /stereomix/i,
  /what u hear/i,
  /wave out mix/i,
  /\bcable output\b/i,
  /vb-audio/i,
  /voicemeeter/i,
  /blackhole/i,
  /soundflower/i,
  /^monitor of /i,
];

export const isLoopbackInputLabel = (label?: string | null): boolean => !!label && LOOPBACK_INPUT_PATTERNS.some((pattern) => pattern.test(label));
